import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import { Select, MenuItem } from "@mui/material";
import SuiInputWithIconRoot from "components/SuiInput/SuiInputWithIconRoot";
import SuiInputIconBoxRoot from "components/SuiInput/SuiInputIconBoxRoot";
import SuiInputIconRoot from "components/SuiInput/SuiInputIconRoot";
import SuiDropdownRoot from "components/SuiDropdown/SuiDropdownRoot"; // Custom styled component
import { useSoftUIController } from "context";

const SuiDropdown = forwardRef(({ size, icon, error, success, disabled, options, ...rest }, ref) => {
  const [controller] = useSoftUIController();
  const { direction } = controller;
  const iconDirection = icon.direction;

  const renderIcon = () => (
    <SuiInputIconBoxRoot ownerState={{ size }}>
      <SuiInputIconRoot fontSize="small" ownerState={{ size }}>
        {icon.component}
      </SuiInputIconRoot>
    </SuiInputIconBoxRoot>
  );

  let template;

  if (icon.component && icon.direction === "left") {
    template = (
      <SuiInputWithIconRoot ref={ref} ownerState={{ error, success, disabled }}>
        {renderIcon()}
        <SuiDropdownRoot
          {...rest}
          ownerState={{ size, error, success, iconDirection, direction, disabled }}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </SuiDropdownRoot>
      </SuiInputWithIconRoot>
    );
  } else if (icon.component && icon.direction === "right") {
    template = (
      <SuiInputWithIconRoot ref={ref} ownerState={{ error, success, disabled }}>
        <SuiDropdownRoot
          {...rest}
          ownerState={{ size, error, success, iconDirection, direction, disabled }}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </SuiDropdownRoot>
        {renderIcon()}
      </SuiInputWithIconRoot>
    );
  } else {
    template = (
      <SuiDropdownRoot {...rest} ref={ref} ownerState={{ size, error, success, disabled }}>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </SuiDropdownRoot>
    );
  }

  return template;
});

SuiDropdown.defaultProps = {
  size: "medium",
  icon: {
    component: null,
    direction: "none",
  },
  error: false,
  success: false,
  disabled: false,
};

SuiDropdown.propTypes = {
  size: PropTypes.oneOf(["small", "medium", "large"]),
  icon: PropTypes.shape({
    component: PropTypes.node,
    direction: PropTypes.oneOf(["none", "left", "right"]),
  }),
  error: PropTypes.bool,
  success: PropTypes.bool,
  disabled: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default SuiDropdown;
